import { Instance, SnapshotIn, types } from 'mobx-state-tree'
import { baseModel } from 'store/utils/base-model'
import { createStore } from 'store/utils/create-store'
import { DateType } from 'store/utils/date-type'
import { getRootStore } from 'store/utils/get-root-store'
import type { RatingScaleLabel } from '../checkins'

// A per-checkin snapshot of a performance question + the employee's answer,
// used read-only on the check-in summary page.
//
// The parent check-in is held as a plain `checkinId` (not an MST
// `reference(Checkin)`) on purpose: a value-level import of the Checkin model
// here would create a circular module dependency (checkins <-> this module)
// that breaks store init at load time. `RatingScaleLabel` is imported as a
// type only (erased at runtime), so no runtime cycle exists.
export const CheckinPerformanceQuestion = baseModel(
  'checkin_performance_questions',
)
  .props({
    checkinId: types.string,
    body: types.string,
    questionType: types.union(
      types.literal('open_dialogue'),
      types.literal('rating_scale'),
    ),
    position: types.maybeNull(types.number),
    answerText: types.maybeNull(types.string),
    answerScaleValue: types.maybeNull(types.number),
    answeredAt: types.maybeNull(DateType),
    teamId: types.maybeNull(types.string),
  })
  .views((self) => ({
    // An answer exists when the type-appropriate column is filled. A rating of
    // 0 ("Meeting") IS a real answer, so test against null/undefined, not truthiness.
    get answered(): boolean {
      if (self.questionType === 'rating_scale') {
        return (
          self.answerScaleValue !== null && self.answerScaleValue !== undefined
        )
      }
      return !!self.answerText && self.answerText.trim() !== ''
    },
    // The chosen rating-scale label (name/colour), resolved from the parent
    // check-in's label set by scale_value. Undefined for open_dialogue or when
    // unanswered.
    get answerLabel(): RatingScaleLabel | undefined {
      if (
        self.answerScaleValue === null ||
        self.answerScaleValue === undefined
      ) {
        return undefined
      }
      const checkin = getRootStore(self).checkins.byId(self.checkinId)
      return checkin?.ratingScaleLabels?.find(
        (label) => label.scaleValue === self.answerScaleValue,
      )
    },
  }))

export type CheckinPerformanceQuestionModelAttributes = SnapshotIn<
  typeof CheckinPerformanceQuestion
>

export interface CheckinPerformanceQuestion extends Instance<
  typeof CheckinPerformanceQuestion
> {}

export const CheckinPerformanceQuestionStore = createStore<
  typeof CheckinPerformanceQuestion
>('CheckinPerformanceQuestion', CheckinPerformanceQuestion)
